import { useAppDispatch, useAppSelector } from 'src/store';
import { useCallback } from 'react';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { getCurrentAddSiteToPlatformFormState } from 'src/app/habitat-insights/state/addSiteToPlatformSelector';
import {
    useHabitatInsightsUploadMutation,
    useLazyHabitatInsightsProjectsQuery,
} from 'src/app/habitat-insights/state/api/habitatInsightsUploadGraphSlice';
import { AddSiteToPlatformFormState, setFormState } from 'src/app/habitat-insights/state/addSiteToPlatformSlice';

const useAddSiteToPlatform = () => {
    const formState = useAppSelector(getCurrentAddSiteToPlatformFormState);
    const { currentCustomerId } = useCustomer();
    const dispatch = useAppDispatch();
    const navigate = useAppNavigation();

    const onFormChange = useCallback(
        (fragment: Partial<AddSiteToPlatformFormState>) => {
            dispatch(
                setFormState({
                    ...fragment,
                })
            );
        },
        [dispatch]
    );

    const { sites } = formState;

    const enableUpload = formState.projectId;

    const [habitatInsightsUpload] = useHabitatInsightsUploadMutation();
    const [fetchHabitatInsightsProjects] = useLazyHabitatInsightsProjectsQuery();

    const submitHabitatInsightsUploadForm = async (): Promise<{ success: boolean; error?: string }> => {
        let response;
        try {
            response = await habitatInsightsUpload({
                portfolioId: formState.portfolioId || undefined,
                isHlcaProject: formState.isHlcaProject,
                project: {
                    projectId: formState.projectId || '',
                    customerId: formState.customerId || (formState.customerName ? undefined : currentCustomerId),
                    customerName: formState.customerName || undefined,
                    projectName: formState.projectName || '',
                    country: formState.country || '',
                },
                sites,
                runCloudJobs: formState.runCloudJobs,
            }).unwrap();

            // Refresh projects list
            await fetchHabitatInsightsProjects({
                customerId: currentCustomerId,
            }).unwrap();

            if (response) {
                // Small delay to show success message before navigation
                setTimeout(() => {
                    navigate.toProjects();
                }, 1500);
                return { success: true };
            }

            return { success: false, error: 'Submission completed but no response received' };
        } catch (error: any) {
            console.error('Submission error:', error);
            const errorMessage =
                error?.data?.message || error?.message || 'Failed to submit sites. Please check your connection and try again.';
            await fetchHabitatInsightsProjects({
                customerId: currentCustomerId,
            }).unwrap();
            return { success: false, error: errorMessage };
        } finally {
            onFormChange({ loading: false });
        }
    };

    return {
        submitHabitatInsightsUploadForm,
        onFormChange,
        formState,
        enableUpload,
    };
};

export default useAddSiteToPlatform;
